import React, { useEffect, useState, useCallback } from 'react';
import { Button, Col, DatePicker, Drawer, Form, Input, InputNumber, Row, Select, Switch, Checkbox } from 'antd';
import { FormInstance } from 'antd/es/form/Form';
import S3Upload, { AllowedFormats } from '@a4b/ui/src/modules/monetisation/puja/molecules/S3Upload';
import TextArea from 'antd/lib/input/TextArea';
import { useSearchParams } from 'react-router-dom';
import { DeleteOutlined, PlusCircleOutlined, DragOutlined } from '@ant-design/icons';
import { Product } from '@a4b/api/src/modules/Monetisation/Puja/types';
import SelectPickupAddress from '../SelectPickupAddress';
import { ONLY_ALPHA_NUMARIC_RULE_MAX_10_LEN } from '@/utils/form.validation';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useAppContext } from '@/components/AppContext';
import { A4B_APPS } from '@/utils/constants';

type AddedItems = { added: Product[] };
interface Props {
    showItemCreation: boolean;
    closeItemCreation: () => void;
    initialValues: any;
    form: FormInstance<any>;
    updateRecord: (nameList: any) => void;
    cdnUrl: string;
    preSignUrl: string;
    service_type: string;
    addedItems: AddedItems;
    storeId?: string;
}

const InventoryForm: React.FC<Props> = ({
    showItemCreation,
    closeItemCreation,
    initialValues,
    form,
    updateRecord,
    cdnUrl,
    preSignUrl,
    service_type,
    addedItems,
    storeId,
}) => {
    const [onFieldChanged, setOnFieldChanged] = useState<boolean>(false);
    const [searchParams] = useSearchParams();
    const { Option } = Select;

    const [storeList, setStoreList] = useState<any[]>([]);
    const [productList, setProductList] = useState<any[]>([]);
    const [storeListLoading, setStoreListLoading] = useState(false);
    const [productListLoading, setProductListLoading] = useState(false);
    const { networkInstance, countryState, app } = useAppContext()
    const monetisationOfferingsApi = app === A4B_APPS.SRIMANDIR ? networkInstance.clientWithHeaders.monetisationOfferingsApi : networkInstance.clientWithHeaders.monetisationOfferingsApi;
    const monetisationPujaApi = app === A4B_APPS.SRIMANDIR ? networkInstance.clientWithHeaders.monetisationPujaApi : networkInstance.clientWithHeaders.monetisationPujaApi;

    const search = ''; // Placeholder for search variable from parent scope if needed.
    const originStoreId = Form.useWatch('origin_store_id', form);

    // Reset states when drawer is closed
    const handleCloseDrawer = () => {
        // Reset all state variables
        setOnFieldChanged(false);
        setStoreList([]);
        setProductList([]);
        setStoreListLoading(false);
        setProductListLoading(false);

        // Call the parent close function
        closeItemCreation();
    };

    // Single function to fetch products from store
    const fetchProducts = useCallback(async (storeId: string) => {
        setProductListLoading(true);
        try {
            const response = await monetisationPujaApi.storesApi.getProductList(
                storeId,
                countryState.countryCode,
                'position',
                true
            );
            if (response?.data) {
                setProductList(response?.data?.data?.products as any || []);
            }
        } catch (err) {
            console.error('Failed to fetch products:', err);
            setProductList([]);
        }
        setProductListLoading(false);
    }, [monetisationPujaApi.storesApi, countryState.countryCode]);

    useEffect(() => {
        const fetchStoreList = async () => {
            setStoreListLoading(true);
            try {
                const response = await monetisationOfferingsApi.storesApi.getStoreList(
                    undefined, 'manage_stores', countryState?.countryCode, '', searchParams.get("language_code") || "hi",
                    'position', undefined, undefined, 'offerings', search || undefined, undefined
                );
                if (response?.data) {
                    setStoreList(response.data.data?.store_list || []);
                }
            } catch (err) {
                console.error('Failed to fetch store list:', err);
                setStoreList([]);
            }
            setStoreListLoading(false);
        };

        // Only fetch store list when drawer is open and item_type is 'item'
        if (showItemCreation && initialValues?.item_type === 'item') {
            fetchStoreList();
        } else if (!showItemCreation) {
            // Clear store list when drawer is closed
            setStoreList([]);
            setProductList([]);
        }
    }, [countryState, searchParams, search, showItemCreation, initialValues?.item_type, monetisationOfferingsApi.storesApi]);

    const handleStoreChange = (storeId: string) => {
        form.setFieldsValue({ offering_sku_id: undefined });
        setProductList([]);
        if (storeId) {
            fetchProducts(storeId);
        }
    };

    const handleValueChange = (newValue: any) => {
        newValue && setOnFieldChanged(true);
    };

    useEffect(() => {
        if (showItemCreation) {
            setOnFieldChanged(false);

            const infoBulletsData = initialValues?.info_bullets && Array.isArray(initialValues.info_bullets)
                ? initialValues.info_bullets.map((bullet: any) => ({
                    text: bullet?.text || ''
                }))
                : [{ text: '' }]

            const recommendedSkusArray = initialValues?.recommended_skus && Array.isArray(initialValues.recommended_skus)
                ? initialValues.recommended_skus.map((id: any) => String(id))
                : [];

            const { recommended_skus: _ignoreRecommendedSkus, ...otherInitialValues } = initialValues || {};

            form.setFieldsValue({
                ...otherInitialValues,
                service_type: service_type,
                batch_size: initialValues?.item_type === 'pooja' ? 200 : initialValues?.batch_size,
                info_bullets: infoBulletsData,
                recommended_skus: recommendedSkusArray,
            });


            if (initialValues?.origin_store_id && initialValues?.item_type === 'item') {
                fetchProducts(initialValues.origin_store_id);
            } else {
                setProductList([]);
            }

            if (initialValues?.item_type === 'pooja' && storeId) {
                fetchProducts(storeId);
            } else if (initialValues?.item_type === 'pooja') {
                setProductList([]);
            }
        }
    }, [form, initialValues, service_type, showItemCreation, fetchProducts, storeId]);

    // Validate and filter recommended_skus: remove products that don't exist or are inactive
    useEffect(() => {
        if (showItemCreation &&
            initialValues?.item_type === 'pooja' &&
            !productListLoading &&
            productList.length > 0) {

            const currentRecommendedSkus = form.getFieldValue('recommended_skus') || [];

            if (currentRecommendedSkus.length === 0) {
                return;
            }

            const activeItemProducts = productList.filter(
                (product: any) => product.item_type === 'item' && product.active !== false
            );

            const validActiveProductIds = new Set(
                activeItemProducts.map((product: any) => String(product.id))
            );

            const validatedSkus = currentRecommendedSkus
                .map((skuId: any) => String(skuId))
                .filter((skuId: string) => validActiveProductIds.has(skuId));

            if (JSON.stringify(validatedSkus.sort()) !== JSON.stringify(currentRecommendedSkus.sort())) {
                form.setFieldsValue({
                    recommended_skus: validatedSkus
                });
                setOnFieldChanged(true);
            }
        }
    }, [showItemCreation, productList, productListLoading, initialValues?.item_type, form]);


    const currentItemType = Form.useWatch('item_type', form);

    useEffect(() => {
        if (currentItemType === 'pooja') {
            form.setFieldsValue({
                origin_store_id: undefined,
                offering_sku_id: undefined
            });
            setProductList([]);
        }
    }, [currentItemType, form]);


    return (
        <>
            <Drawer
                title="Update inventory"
                placement='right'
                onClose={handleCloseDrawer} // Use the new close handler
                visible={showItemCreation}
                destroyOnClose={true} // This ensures component is fully destroyed on close
                size='large'
            >
                <Form
                    form={form}
                    layout='vertical'
                    onFinish={updateRecord}
                >
                    <Col>
                        <Row justify='space-between' style={{ width: '100%' }}>
                            <Form.Item
                                label='ID'
                                name='id'
                                style={{ flexBasis: '48%' }}
                            >
                                <Input style={{ color: 'black' }} disabled></Input>
                            </Form.Item>
                            <Form.Item
                                label='Title'
                                name='title'
                                labelCol={{ span: 28 }}
                                wrapperCol={{ span: 28 }}
                                rules={[{ required: true, message: 'Enter Title' }]}
                                style={{ flexBasis: '48%' }}
                            >
                                <Input placeholder='Enter Title' onChange={(e) => handleValueChange(e.target.value)} />
                            </Form.Item>
                        </Row>

                        <Row justify='space-between' style={{ width: '100%' }}>
                            <Form.Item
                                label='Description'
                                name='description'
                                labelCol={{ span: 28 }}
                                wrapperCol={{ span: 28 }}
                                rules={[
                                    {
                                        required: form.getFieldValue('item_type') === 'item',
                                        message: 'Enter Description',
                                    },
                                ]}
                                style={{ flexBasis: '48%' }}
                            >
                                <TextArea placeholder='Enter Description' rows={3} onChange={(e) => handleValueChange(e.target.value)} />
                            </Form.Item>
                            <Form.Item
                                label='Short Name'
                                name='short_name'
                                labelCol={{ span: 32 }}
                                wrapperCol={{ span: 32 }}
                                style={{ flexBasis: '48%' }}
                                rules={[{ required: false, message: 'Enter Short Name' }]}
                            >
                                <Input placeholder='Enter Short Name' onChange={(e) => handleValueChange(e.target.value)} />
                            </Form.Item>
                        </Row>

                        <Form.Item
                            label='sub type'
                            name='sub_type'
                            style={{ display: 'none' }}
                        >
                            <Input />
                        </Form.Item>

                        {/* Only show store and SKU fields for item type 'item' */}
                        <Form.Item shouldUpdate={() => true} noStyle>
                            {({ getFieldValue }) =>
                                getFieldValue('item_type') === 'item' && getFieldValue('sub_type') === 'chadava' && (
                                    <Row justify='space-between' style={{ width: '100%' }}>
                                        <Form.Item
                                            label='Chadhava Store Id'
                                            name='origin_store_id'
                                            rules={[{ required: getFieldValue('sub_type') === 'chadava', message: 'Please select a store id' }]}
                                            style={{ flexBasis: '48%' }}
                                        >
                                            {getFieldValue('sub_type') === 'chadava' &&
                                                getFieldValue('origin_store_id') &&
                                                getFieldValue('offering_sku_id') ? (
                                                <Input
                                                    value={getFieldValue('origin_store_id')}
                                                    readOnly
                                                    style={{ color: 'black', backgroundColor: '#f5f5f5' }}
                                                />
                                            ) : (
                                                <Select
                                                    showSearch
                                                    placeholder='Select a store'
                                                    loading={storeListLoading}
                                                    onChange={handleStoreChange}
                                                    allowClear
                                                    filterOption={(input, option) =>
                                                        (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                                                    }
                                                >
                                                    {storeList.map(store => (
                                                        <Option key={store?.id} value={store?.id}>
                                                            {store?.id}
                                                        </Option>
                                                    ))}
                                                </Select>
                                            )}
                                        </Form.Item>

                                        <Form.Item
                                            label='Chadhava SKU Id'
                                            name='offering_sku_id'
                                            rules={[{ required: getFieldValue('sub_type') === 'chadava', message: 'Please select an offering SKU!' }]}
                                            style={{ flexBasis: '48%' }}
                                        >
                                            {getFieldValue('sub_type') === 'chadava' &&
                                                getFieldValue('origin_store_id') &&
                                                getFieldValue('offering_sku_id') ? (
                                                <Form.Item noStyle shouldUpdate={() => true}>
                                                    {({ getFieldValue: getCurrentValue }) => {
                                                        const skuId = getCurrentValue('offering_sku_id');
                                                        const selectedProduct = productList?.find(product => product?.id === skuId);
                                                        const displayValue = selectedProduct ? `${selectedProduct?.title} - ${selectedProduct?.id}` : skuId;
                                                        return (
                                                            <Input
                                                                value={displayValue}
                                                                readOnly
                                                                style={{ color: 'black', backgroundColor: '#f5f5f5' }}
                                                            />
                                                        );
                                                    }}
                                                </Form.Item>
                                            ) : (
                                                <Select
                                                    showSearch
                                                    placeholder={!originStoreId ? 'Please select a store first' : 'Select a SKU'}
                                                    loading={productListLoading}
                                                    disabled={!originStoreId || productListLoading}
                                                    allowClear
                                                    filterOption={(input, option) => {
                                                        const children = option?.children as unknown as string;
                                                        return children?.toLowerCase().includes(input.toLowerCase());
                                                    }}
                                                    optionFilterProp="children"
                                                >
                                                    {productList?.map(product => (
                                                        <Option key={product?.id} value={product?.id}>
                                                            <span style={{ fontWeight: 'bold' }}>{product?.title}</span> - {product?.id}
                                                        </Option>
                                                    ))}
                                                </Select>
                                            )}
                                        </Form.Item>
                                    </Row>
                                )
                            }
                        </Form.Item>

                        <Row style={{ width: '100%' }}>
                            <Form.Item shouldUpdate={() => true} style={{ flexBasis: '20%' }}>
                                {({ getFieldValue }) => (
                                    <Form.Item
                                        label='Batch size'
                                        name='batch_size'
                                        labelCol={{ span: 18 }}
                                        wrapperCol={{ span: 18 }}
                                        rules={[{ required: true, message: 'Enter Batch size' }]}
                                        style={{ flexBasis: '20%' }}
                                    >
                                        <InputNumber disabled={getFieldValue("item_type") === 'pooja' || getFieldValue('sub_type') === 'chadava'} placeholder='Enter Batch size' onChange={(e) => handleValueChange(e)} />
                                    </Form.Item>
                                )}
                            </Form.Item>

                            <Form.Item
                                label='Quantities'
                                name='quantities'
                                labelCol={{ span: 14 }}
                                wrapperCol={{ span: 14 }}
                                rules={[{ required: true, message: 'Enter quantities' }]}
                                style={{ flexBasis: '20%' }}
                            >
                                <InputNumber placeholder='Enter quantities' onChange={(e) => handleValueChange(e)} />
                            </Form.Item>

                            <Form.Item
                                label='Currency'
                                name='currency'
                                labelCol={{ span: 32 }}
                                wrapperCol={{ span: 32 }}
                                rules={[{ required: true, message: 'Select Currency' }]}
                                style={{ flexBasis: '20%' }}
                            >
                                <Select
                                    style={{ width: '80%' }}
                                    placeholder='Enter price'
                                    onChange={(e) => handleValueChange(e)}
                                >
                                    <Option value='INR'>INR</Option>
                                    <Option value='USD'>USD</Option>
                                </Select>
                            </Form.Item>

                            <Form.Item
                                label='Price'
                                name='price'
                                labelCol={{ span: 12 }}
                                wrapperCol={{ span: 12 }}
                                rules={[{ required: true, message: 'Enter price' }]}
                                style={{ flexBasis: '20%' }}
                            >
                                <InputNumber placeholder='Enter price' onChange={(e) => handleValueChange(e)} />
                            </Form.Item>

                            <Form.Item
                                label='Position'
                                name='position'
                                labelCol={{ span: 12 }}
                                wrapperCol={{ span: 12 }}
                                rules={[{ required: true, message: 'Enter position' }]}
                                style={{ flexBasis: '20%' }}
                            >
                                <InputNumber placeholder='Enter position' onChange={(e) => handleValueChange(e)} />
                            </Form.Item>
                        </Row>

                        <Form.Item shouldUpdate={() => true} style={{ width: '100%' }}>
                            {({ getFieldValue }) => (
                                (getFieldValue("item_type") === 'item' && searchParams?.get('service_type') === 'pooja') ?
                                    <Form.Item
                                        label='Campaigns'
                                        style={{ width: '100%', marginBottom: '0px' }}
                                        labelCol={{ span: 32 }}
                                        wrapperCol={{ span: 32 }}
                                        rules={[{ required: false, message: 'Please input Campaign' }]}
                                        name='campaignList'
                                        hidden
                                    >
                                        <Select
                                            mode="multiple"
                                            size='middle'
                                            placeholder="Please select"
                                            defaultValue={[]}
                                            style={{ width: '100%' }}
                                            allowClear
                                            onChange={() => handleValueChange("Selected")}
                                        >
                                            {initialValues?.campaignArray?.map((id: any) => (
                                                <Select.Option key={id[1]} value={id[1]} onChange={(e: any) => handleValueChange(e.target)}>
                                                    {id[1]}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item> :
                                    <Form.Item
                                        label='Campaigns'
                                        style={{ width: '100%', marginBottom: '0px' }}
                                        labelCol={{ span: 32 }}
                                        wrapperCol={{ span: 32 }}
                                        rules={[{ required: false, message: 'Please input Campaign' }]}
                                        name='campaignList'
                                    >
                                        <Select
                                            mode="multiple"
                                            size='middle'
                                            placeholder="Please select"
                                            defaultValue={[]}
                                            style={{ width: '100%' }}
                                            allowClear
                                            onChange={() => handleValueChange("Selected")}
                                        >
                                            {initialValues?.campaignArray?.map((id: any) => (
                                                <Select.Option key={id[1]} value={id[1]} onChange={(e: any) => handleValueChange(e.target)}>
                                                    {id[1]}
                                                </Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                            )}
                        </Form.Item>

                        <Row>
                            <Form.Item
                                name='multi_add'
                                label='Multi Add'
                                rules={[{ required: false, message: 'Please input multi add' }]}
                                valuePropName='checked'
                            >
                                <Switch />
                            </Form.Item>
                            <Form.Item
                                style={{ marginLeft: '30px' }}
                                name='active'
                                label='Status'
                                valuePropName='checked'
                                rules={[{ required: false }]}
                            >
                                <Switch />
                            </Form.Item>
                            <Form.Item
                                style={{ marginLeft: '30px' }}
                                name='bestseller'
                                label='Best Seller'
                                valuePropName='checked'
                                rules={[{ required: false }]}
                            >
                                <Switch />
                            </Form.Item>
                            <Form.Item
                                style={{ marginLeft: '30px' }}
                                name='deliverable'
                                label='Deliverable'
                                valuePropName='checked'
                                rules={[{ required: false }]}
                            >
                                <Switch />
                            </Form.Item>
                            <Form.Item shouldUpdate={() => true}>
                                {({ getFieldValue }) =>
                                    getFieldValue("item_type") === 'pooja' &&
                                    <Form.Item
                                        style={{ marginLeft: '30px' }}
                                        name='recommended'
                                        label='Recommended'
                                        valuePropName='checked'
                                        rules={[{ required: false }]}
                                    >
                                        <Switch />
                                    </Form.Item>
                                }
                            </Form.Item>
                        </Row>
                        <Row>
                            <Form.Item
                                name='item_type'
                                label='Item type'
                                rules={[{ required: false }]}
                                hidden
                            >
                                <Input />
                            </Form.Item>
                            <Form.Item shouldUpdate={() => true}>
                                {({ getFieldValue }) =>
                                    getFieldValue("item_type") === 'pooja' &&
                                    <Form.Item
                                        label='Member size'
                                        name='member_size'
                                        style={{ flexBasis: '15%' }}
                                        rules={[{ required: true, message: 'Enter Member size' }]}
                                    >
                                        <InputNumber placeholder='Enter Member size' onChange={(e) => handleValueChange(e)} />
                                    </Form.Item>
                                }
                            </Form.Item>

                            <Form.Item shouldUpdate={() => true}>
                                {({ getFieldValue }) =>
                                    getFieldValue("item_type") === 'pooja' &&
                                    <Form.Item
                                        label='Specificity'
                                        name='specificity'
                                        style={{ flexBasis: '25%', marginLeft: '15px' }}
                                        rules={[{ required: true, message: 'Enter Specificity' }]}
                                    >
                                        <Input placeholder='Enter Specificity' onChange={(e) => handleValueChange(e.target.value)} />
                                    </Form.Item>
                                }
                            </Form.Item>
                        </Row>

                        <Row style={{ width: '100%' }}>
                            <Form.Item
                                label='Item Image'
                                name='image_url'
                                labelCol={{ span: 24 }}
                                wrapperCol={{ span: 24 }}
                                valuePropName='fileList'
                                rules={[{ min: 1, max: 1, validator: async (rules: any, values: any) => { if (values?.length < rules.min) { return Promise.reject('Upload Item Image') } } }]}
                            >
                                <S3Upload
                                    maxSizeInKiloBytes={300} cdnUrl={cdnUrl} preSignUrl={preSignUrl} min={1} max={1} allowedFileExtension={[AllowedFormats.JPEG, AllowedFormats.PNG, AllowedFormats.WEBP, AllowedFormats.SVG]}
                                    onChange={(e: any) => handleValueChange(e)} />
                            </Form.Item>

                            <Form.Item shouldUpdate={() => true}>
                                {({ getFieldValue }) =>
                                    (getFieldValue("item_type") === 'pooja') &&
                                    <Form.Item
                                        name='process_date'
                                        label='Process Date'
                                        labelCol={{ span: 24 }}
                                        wrapperCol={{ span: 24 }}
                                        style={{ marginLeft: '20px' }}
                                        rules={[{ required: false, message: 'Pick Date' }]}
                                    >
                                        <DatePicker showTime />
                                    </Form.Item>
                                }
                            </Form.Item>
                        </Row>

                        <Form.Item shouldUpdate={() => true}>
                            {({ getFieldValue }) =>
                                getFieldValue("item_type") === 'pooja' &&
                                <Row style={{ border: '1px solid #e0dcdc', padding: '10px', borderRadius: '5px' }}>
                                    <Row style={{ width: '100%', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <h5 style={{ fontSize: "16px", marginBottom: 0 }}>
                                            Package Details
                                        </h5>
                                        <Form.Item
                                            name="show_additional_info"
                                            valuePropName="checked"
                                            style={{ marginBottom: 0 }}
                                        >
                                            <Checkbox>
                                                Package Details for new version
                                            </Checkbox>
                                        </Form.Item>
                                    </Row>
                                    <Row style={{ width: '100%', justifyContent: 'space-between' }}>
                                        <Form.Item
                                            label='Bullet Icon'
                                            name={['additional_info', 'iconImg']}
                                            labelCol={{ span: 24 }}
                                            wrapperCol={{ span: 24 }}
                                            valuePropName='fileList'
                                            style={{ marginBottom: '0px' }}
                                            rules={[{ min: 1, max: 1, validator: async (rules: any, values: any) => { if (values?.length < rules.min) { return Promise.reject('Upload Icon') } } }]}
                                        >
                                            <S3Upload cdnUrl={cdnUrl} preSignUrl={preSignUrl} min={1} max={1} onChange={(e: any) => handleValueChange(e)} />
                                        </Form.Item>
                                        <Form.Item shouldUpdate={() => true} style={{ width: '80%' }}>
                                            {({ getFieldValue }) =>
                                                getFieldValue(['additional_info', 'error']) === true &&
                                                <Form.Item
                                                    label='Additional Info'
                                                    name={['additional_info', 'additionInfo']}
                                                    labelCol={{ span: 24 }}
                                                    wrapperCol={{ span: 24 }}
                                                    style={{ marginBottom: '0px', width: '100%' }}
                                                >
                                                    <TextArea rows={4} style={{ paddingRight: '30px' }} />
                                                </Form.Item>}
                                        </Form.Item>
                                    </Row>
                                    <Form.List name={['additional_info', 'list']}>
                                        {(fields, { add, remove, move }) => (
                                            <DragDropContext onDragEnd={({ source, destination }) => { if (destination) { move(source.index, destination.index); } }}>
                                                <Droppable droppableId="additional-info-list">
                                                    {(provided) => (
                                                        <div {...provided.droppableProps} ref={provided.innerRef} style={{ width: '100%' }}>
                                                            <Col span={24} style={{ width: '100%' }}>
                                                                {fields.map((field: any, index: number) => (
                                                                    <Draggable key={field.key} draggableId={field.key.toString()} index={index}>
                                                                        {(provided) => (
                                                                            <div
                                                                                ref={provided.innerRef}
                                                                                {...provided.draggableProps}
                                                                                style={{ ...provided.draggableProps.style, paddingRight: '12px', paddingTop: '10px' }}
                                                                            >
                                                                                <Col key={field.key} style={{ paddingRight: '12px', paddingTop: '10px' }}>
                                                                                    <div style={{ border: '1px dashed #e0dcdc', padding: '10px' }}>
                                                                                        <Row justify="space-between" style={{ width: '100%' }}>
                                                                                            <div {...provided.dragHandleProps} style={{ cursor: 'move', marginRight: '8px', marginTop: '30px' }}><DragOutlined /></div>
                                                                                            <Form.Item
                                                                                                label={`Point ${index + 1}`}
                                                                                                name={[field.name]}
                                                                                                labelCol={{ span: 28 }}
                                                                                                wrapperCol={{ span: 28 }}
                                                                                                style={{ flexBasis: '85%', marginBottom: '0px' }}
                                                                                                rules={[{ required: true, message: 'Enter Additional Info' }]}
                                                                                            >
                                                                                                <TextArea rows={2} placeholder="Enter Additional Info" />
                                                                                            </Form.Item>
                                                                                            <Button onClick={() => { remove(field.name); }} type="ghost" icon={<DeleteOutlined />} size="middle" danger style={{ marginTop: '30px' }} />
                                                                                        </Row>
                                                                                    </div>
                                                                                </Col>
                                                                            </div>
                                                                        )}
                                                                    </Draggable>
                                                                ))}
                                                                {provided.placeholder}
                                                            </Col>
                                                        </div>
                                                    )}
                                                </Droppable>
                                                <Col span={24} style={{ paddingTop: '15px' }}>
                                                    <Row justify="start">
                                                        <Col span={4}>
                                                            <Button onClick={() => { add() }} type="ghost" icon={<PlusCircleOutlined />} size="middle">
                                                                Add Additional Info
                                                            </Button>
                                                        </Col>
                                                    </Row>
                                                </Col>
                                            </DragDropContext>
                                        )}
                                    </Form.List>
                                </Row>
                            }
                        </Form.Item>
                        <Form.Item shouldUpdate={() => true}>
                            {({ getFieldValue }) =>
                                getFieldValue("item_type") === 'pooja' &&
                                <Row style={{ border: '1px solid #e0dcdc', padding: '10px', borderRadius: '5px' }}>
                                    <Form.Item
                                        label='Package Highlights'
                                        labelCol={{ span: 24 }}
                                        wrapperCol={{ span: 24 }}
                                        style={{ marginBottom: '5px', width: '100%' }}
                                    />
                                    <Form.List name="info_bullets">
                                        {(fields, { add, remove, move }) => (
                                            <DragDropContext onDragEnd={({ source, destination }) => { if (destination) { move(source.index, destination.index); } }}>
                                                <Droppable droppableId="info-bullets-list">
                                                    {(provided) => (
                                                        <div {...provided.droppableProps} ref={provided.innerRef} style={{ width: '100%' }}>
                                                            <Col span={24} style={{ width: '100%' }}>
                                                                {fields.map((field: any, index: number) => (
                                                                    <Draggable key={field.key} draggableId={field.key.toString()} index={index}>
                                                                        {(provided) => (
                                                                            <div
                                                                                ref={provided.innerRef}
                                                                                {...provided.draggableProps}
                                                                                style={{ ...provided.draggableProps.style, paddingRight: '12px', paddingTop: '5px' }}
                                                                            >
                                                                                <Col key={field.key} style={{ paddingRight: '12px', paddingTop: '5px' }}>
                                                                                    <div style={{ border: '1px dashed #e0dcdc', padding: '10px' }}>
                                                                                        <Row justify="space-between" style={{ width: '100%' }}>
                                                                                            <div {...provided.dragHandleProps} style={{ cursor: 'move', marginRight: '8px', marginTop: '10px' }}><DragOutlined /></div>
                                                                                            <Form.Item
                                                                                                label={`Bullet ${index + 1}`}
                                                                                                name={[field.name, 'text']}
                                                                                                labelCol={{ span: 28 }}
                                                                                                wrapperCol={{ span: 28 }}
                                                                                                style={{ flexBasis: '85%', marginBottom: '0px' }}
                                                                                                rules={[
                                                                                                    { required: true, message: 'Enter bullet text' },
                                                                                                    {
                                                                                                        validator: (_, value) => {
                                                                                                            if (!value) return Promise.resolve();
                                                                                                            if (value.length > 20) {
                                                                                                                return Promise.reject('Maximum 20 characters allowed');
                                                                                                            }
                                                                                                            return Promise.resolve();
                                                                                                        }
                                                                                                    }
                                                                                                ]}
                                                                                            >
                                                                                                <TextArea rows={2} placeholder="Enter bullet point (max 20 characters)" />
                                                                                            </Form.Item>
                                                                                            <Button onClick={() => { remove(field.name); }} type="ghost" icon={<DeleteOutlined />} size="middle" danger style={{ marginTop: '40px' }} />
                                                                                        </Row>
                                                                                    </div>
                                                                                </Col>
                                                                            </div>
                                                                        )}
                                                                    </Draggable>
                                                                ))}
                                                                {provided.placeholder}
                                                            </Col>
                                                        </div>
                                                    )}
                                                </Droppable>
                                                <Col span={24} style={{ paddingTop: '15px' }}>
                                                    <Row justify="start">
                                                        <Col span={4}>
                                                            <Button onClick={() => { add() }} type="ghost" icon={<PlusCircleOutlined />} size="middle">
                                                                Add Bullet
                                                            </Button>
                                                        </Col>
                                                    </Row>
                                                </Col>
                                            </DragDropContext>
                                        )}
                                    </Form.List>
                                </Row>
                            }
                        </Form.Item>
                        <Form.Item shouldUpdate={(prevValues, currentValues) => prevValues.deliverable !== currentValues.deliverable} noStyle>
                            {({ getFieldValue }) => {
                                const isDeliverable = getFieldValue("deliverable") === true;
                                if (isDeliverable) {
                                    return <Row style={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
                                        <Col span={32}>
                                            <SelectPickupAddress formItemName='pickup_address' isRequired={isDeliverable} />
                                        </Col>
                                        <Col span={32}>
                                            <Form.Item
                                                label={`Sku code`}
                                                name='sku_code'
                                                labelCol={{ span: 28 }}
                                                wrapperCol={{ span: 28 }}
                                                style={{ flexBasis: '85%', marginBottom: '0px' }}
                                                rules={[{ required: true, message: 'SKU Code is required' }, ONLY_ALPHA_NUMARIC_RULE_MAX_10_LEN]}
                                            >
                                                <Input placeholder="Enter SKU Code" />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                }
                            }}
                        </Form.Item>
                        <Form.Item shouldUpdate={true} noStyle>
                            {({ getFieldValue }) => {
                                const itemType = getFieldValue("item_type");
                                return (
                                    <Form.Item
                                        label="Bottomsheet Images"
                                        name="bottomsheet_medias"
                                        labelCol={{ span: 24 }}
                                        wrapperCol={{ span: 24 }}
                                        valuePropName="fileList"
                                        rules={
                                            itemType === "pooja" ? [] : [{
                                                validator: async (_: any, value: any) => {
                                                    if (!value || value.length < 1) {
                                                        return Promise.reject(new Error("Please upload at least 1 image"));
                                                    }
                                                    return Promise.resolve();
                                                },
                                            }]
                                        }
                                    >
                                        <S3Upload cdnUrl={cdnUrl} preSignUrl={preSignUrl} min={1} max={10} />
                                    </Form.Item>
                                );
                            }}
                        </Form.Item>

                        <Row style={{ display: 'flex', gap: '40px' }}>
                            {/* <Form.Item label='Is IGST Applicable?' name='is_igst' valuePropName='checked'>
                                <Switch />
                            </Form.Item> */}

                            <Form.Item label='HSN Code' name='hsn_code'>
                                <Input placeholder='Enter HSN Code' maxLength={8} />
                            </Form.Item>

                            <Form.Item
                                label="Tax Rate"
                                name="tax_rate"
                                rules={[{
                                    validator: (_, value) => {
                                        if (value === undefined || value === null || value === "") {
                                            return Promise.resolve();
                                        }
                                        const numValue = Number(value);
                                        if (isNaN(numValue) || numValue < 0 || numValue > 100) {
                                            return Promise.reject("Tax Rate % must be a valid number between 0 and 100!");
                                        }
                                        return Promise.resolve();
                                    }
                                }]}
                            >
                                <Input type="number" placeholder="Enter Tax Rate %" />
                            </Form.Item>
                        </Row>

                        <Form.Item shouldUpdate={() => true}>
                            {({ getFieldValue }) =>
                                getFieldValue("item_type") === 'pooja' &&
                                <Row style={{ border: '1px solid #e0dcdc', padding: '10px', borderRadius: '5px', marginTop: '20px' }}>
                                    <Form.Item
                                        label='SKU Item Configuration'
                                        labelCol={{ span: 24 }}
                                        wrapperCol={{ span: 24 }}
                                        style={{ marginBottom: '5px', width: '100%' }}
                                    />
                                    <Col span={24} style={{ width: '100%' }}>
                                        <Form.Item shouldUpdate={() => true}>
                                            {() => {
                                                const currentValue = form.getFieldValue('recommended_skus') || [];
                                                return (
                                                    <Form.Item
                                                        name="recommended_skus"
                                                        style={{ width: "100%", marginBottom: 0 }}
                                                        noStyle
                                                    >
                                                        <Select
                                                            placeholder="Select personalization type..."
                                                            mode="multiple"
                                                            style={{ width: "100%" }}
                                                            onChange={(e) => {
                                                                handleValueChange(e);
                                                            }}
                                                            loading={productListLoading}
                                                            value={currentValue}
                                                            optionLabelProp="label"
                                                        >
                                                            {productList
                                                                ?.filter((product: any) => product.item_type === 'item')
                                                                .map((product: any) => {
                                                                    const productIdStr = String(product.id);
                                                                    const isInactive = product.active === false;
                                                                    return (
                                                                        <Select.Option
                                                                            key={productIdStr}
                                                                            value={productIdStr}
                                                                            label={product.title}
                                                                            disabled={isInactive}
                                                                            style={{
                                                                                backgroundColor: isInactive ? '#f5f5f5' : 'transparent',
                                                                                color: isInactive ? '#999' : 'inherit',
                                                                                opacity: isInactive ? 0.6 : 1
                                                                            }}
                                                                        >
                                                                            {product.title} {isInactive ? '(Inactive)' : ''}
                                                                        </Select.Option>
                                                                    );
                                                                })}
                                                        </Select>
                                                    </Form.Item>
                                                );
                                            }}
                                        </Form.Item>
                                    </Col>
                                </Row>
                            }
                        </Form.Item>

                        <Button htmlType="submit" disabled={!onFieldChanged}>Update</Button>
                    </Col>
                </Form>
            </Drawer>
        </>
    );
};

export default InventoryForm;